import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Info,
  MessageSquare,
  Save,
  Send,
  Shield,
  Sparkles,
  User,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import {
  getAiAgentSettings,
  updateAiAgentSettings,
} from "./api/aiAgentApi";
import {
  readAiAgentDraft,
  removeAiAgentDraft,
  writeAiAgentDraft,
} from "./aiAgentDraftStorage";
import {
  sameValue,
  validateSettingsNumbers,
  buildSettingsReview,
  applySettingsReview,
  displaySettingsValue,
} from "./aiAgentSettingsPersistence";
import { capabilityDependencies, capabilityReadiness, newSendingCapabilities, anyLive } from "./aiAgentActivation";
import { AiAgentReadinessSummary, AiAgentActivationDialog } from "./AiAgentActivationStatus";
import { AI_AGENT_SUBMENU_ITEMS, initialAiAgentSection } from "./aiAgentNavigation";

const OPTIONS = {
  industryType: [
    "GeneralBusiness",
    "Education",
    "RetailEcommerce",
    "FinanceLoanInsurance",
    "HealthcareClinic",
    "AutomobileWorkshop",
    "RealEstate",
    "TravelHospitality",
    "SalonBeauty",
    "RestaurantFood",
    "B2BServices",
    "Custom",
  ],
  disclosureMode: [
    "BusinessAssistant",
    "NamedAssistant",
    "AutomatedAssistant",
    "Custom",
  ],
  languageMode: ["AutoDetect", "English", "Hindi", "Hinglish", "Custom"],
  tone: [
    "FriendlyProfessional",
    "Formal",
    "Concise",
    "SalesFocused",
    "Supportive",
    "LuxuryPremium",
  ],
  replyLength: ["Short", "Medium", "Detailed"],
  emojiUsage: ["None", "Minimal", "Friendly"],
  sensitiveTopicPolicy: [
    "StrictHandover",
    "SafeGenericThenHandover",
    "BusinessConfigured",
  ],
  noSourceBehavior: ["NoDraftWarning", "SafeVerifyWithTeamDraft", "Handover"],
  aiAutoReplyMode: ["Off", "DryRunOnly", "InternalBeta", "AutoReplySafeOnly"],
  leadQualificationAutoReplyMode: ["Off", "DryRunOnly", "InternalBeta"],
  leadQualificationPlannerMode: ["Off", "Shadow", "Live"],
  leadQualificationControlledAnswerLiveMode: [
    "Off",
    "Shadow",
    "LiveForSelectedScenarios",
  ],
  naturalReplyMode: ["Off", "Shadow", "DryRun", "Live"],
  leadRestartPolicy: ["AdminOnly", "CustomerConfirmed", "Disabled"],
  responsePolicyMode: [
    "Configured",
    "AiPolished",
    "SourceBacked",
    "UseCardSetting",
  ],
};

const OPTION_LABELS = {
  industryType: {
    GeneralBusiness: "General business",
    Education: "Education",
    RetailEcommerce: "Retail / e-commerce",
    FinanceLoanInsurance: "Finance, loan or insurance",
    HealthcareClinic: "Healthcare / clinic",
    AutomobileWorkshop: "Automobile / workshop",
    RealEstate: "Real estate",
    TravelHospitality: "Travel / hospitality",
    SalonBeauty: "Salon / beauty",
    RestaurantFood: "Restaurant / food",
    B2BServices: "B2B services",
    Custom: "Custom",
  },
  disclosureMode: {
    BusinessAssistant: "Show as a generic business assistant",
    NamedAssistant: "Use the bot name above",
    AutomatedAssistant: "Tell customers it is an automated assistant",
    Custom: "Custom",
  },
  languageMode: {
    AutoDetect: "Auto-detect from customer",
    English: "English only",
    Hindi: "Hindi only",
    Hinglish: "Hinglish (mixed)",
    Custom: "Custom",
  },
  tone: {
    FriendlyProfessional: "Friendly and professional",
    Formal: "Formal",
    Concise: "Concise",
    SalesFocused: "Sales-focused",
    Supportive: "Supportive",
    LuxuryPremium: "Luxury / premium",
  },
  replyLength: {
    Short: "Short (1-2 sentences)",
    Medium: "Medium (short paragraph)",
    Detailed: "Detailed (full paragraph)",
  },
  emojiUsage: {
    None: "No emojis",
    Minimal: "Minimal (occasional)",
    Friendly: "Friendly (use freely)",
  },
  sensitiveTopicPolicy: {
    StrictHandover: "Always hand over to your team",
    SafeGenericThenHandover: "Send a safe generic reply, then hand over",
    BusinessConfigured: "Use the rules I configure below",
  },
  noSourceBehavior: {
    NoDraftWarning: "Do not draft, only show a warning to your team",
    SafeVerifyWithTeamDraft: "Draft a polite 'I will verify and get back' message",
    Handover: "Quietly hand over to your team",
  },
  aiAutoReplyMode: {
    Off: "Off - AI never sends a reply on its own",
    DryRunOnly: "Dry-run only - log decisions, never send",
    InternalBeta: "Internal beta - allowed for test businesses",
    AutoReplySafeOnly: "SafeOnly - controlled customer rollout",
  },
  leadQualificationAutoReplyMode: {
    Off: "Off",
    DryRunOnly: "Dry-run only - evaluate but never send",
    InternalBeta: "Internal beta - allow test sends",
  },
  leadQualificationPlannerMode: {
    Off: "Off — planner disabled, deterministic routing only",
    Shadow: "Shadow — planner runs and logs but does not affect routing",
    Live: "Live — planner decisions drive the lead flow",
  },
  naturalReplyMode: {
    Off: "Off — deterministic replies only",
    Shadow: "Shadow — rephrase runs and logs, reply is unchanged",
    DryRun: "Dry-run — show rephrased preview in UI, never send",
    Live: "Live — send rephrased reply to customers",
  },
  leadRestartPolicy: {
    AdminOnly: "AdminOnly — Only admin can reset/start a fresh lead session",
    CustomerConfirmed: "CustomerConfirmed — Customer can restart after confirmation",
    Disabled: "Disabled — Do not allow customer restart from chat",
  },
};

const DEFAULT_FORM = {
  isEnabled: false,
  draftSuggestionEnabled: true,
  fallbackDryRunEnabled: false,
  outsideFlowAiFallbackEnabled: true,
  autoReplyAiRescueEnabled: false,
  leadQualificationDryRunEnabled: false,
  leadQualificationAutoReplyMode: "DryRunOnly",
  leadQualificationPlannerMode: "Off",
  leadQualificationControlledAnswerLiveMode: "Off",
  naturalReplyMode: null,
  aiAutoReplyMode: "Off",
  requireSourceBackedAutoReply: true,
  maxAiAutoRepliesPerThread: 3,
  maxAiAutoRepliesPerContactPerDay: 5,
  businessHoursOnly: false,
  handoverOnNoSource: true,
  safeNoSourceFallbackEnabled: false,
  approvedMediaAutoSendEnabled: false,
  maxSafeNoSourceFallbacksPerThread: 1,
  handoverOnSensitiveTopic: true,
  botDisplayName: "Business Assistant",
  botRoleName: "Support Assistant",
  disclosureMode: "BusinessAssistant",
  greetingTemplate: "",
  industryType: "GeneralBusiness",
  businessGoal: "",
  languageMode: "AutoDetect",
  tone: "FriendlyProfessional",
  replyLength: "Short",
  emojiUsage: "Minimal",
  requireSourceBackedAnswer: true,
  allowGeneralKnowledgeFallback: false,
  maxRetrievedChunks: 5,
  noSourceBehavior: "NoDraftWarning",
  allowedTopicsNotes: "",
  blockedTopicsNotes: "",
  sensitiveTopicPolicy: "StrictHandover",
  escalationRules: "",
  handoverMessage:
    "I'll connect you with our team so they can help with the correct details.",
  photoReceivedMessage: "",
  leadSessionTimeoutHours: null,
  leadRestartPolicy: "AdminOnly",
  maxAiRepliesPerThread: 5,
  maxAiRepliesPerContactPerDay: 10,
  maxOutputTokens: 300,
  providerTimeoutSeconds: 15,
  maxContextMessages: 20,
  temperature: 0.3,
  additionalInstructions: "",
  locationProofFallbackPolicy: {
    enableLocationOnlyFallback: false,
    enableGenericFallback: false,
    locationOnlyTemplate: "",
    genericFallbackTemplate: "",
  },
};

const CONTROLLED_ANSWER_LIVE_MODE_LABELS = {
  Off: "Off - controlled answers are not sent live",
  Shadow: "Shadow - controlled answers are evaluated and logged only",
  LiveForSelectedScenarios:
    "Live for selected scenarios - sends approved controlled replies for a small allowlist",
};

const RESPONSE_POLICY_MODES = [
  "Configured",
  "AiPolished",
  "SourceBacked",
  "UseCardSetting",
];

const RESPONSE_POLICY_MODE_LABELS = {
  Configured: "Configured",
  AiPolished: "AI-polished",
  SourceBacked: "Source-backed",
  UseCardSetting: "Use card setting",
};

const RESUME_QUESTION_MODE_LABELS = {
  Configured: "Send as written",
  AiPolished: "AI-polish before sending",
};

const BALANCED_RESPONSE_POLICY = {
  leadQuestionMode: "Configured",
  resumeQuestionMode: "Configured",
  controlledAnswerMode: "UseCardSetting",
  fieldObjectionMode: "AiPolished",
  factualKbAnswerMode: "SourceBacked",
  trustAnswerMode: "SourceBacked",
  unknownFallbackMode: "Configured",
  smallTalkMode: "Configured",
  mediaCaptionMode: "Configured",
  reportMessageMode: "Configured",
};

const RESPONSE_POLICY_FIELDS = [
  {
    key: "leadQuestionMode",
    label: "Lead question mode",
  },
  {
    key: "resumeQuestionMode",
    label: "Resume question style",
    help: "How the AI asks the same lead question again after answering a concern.",
    note:
      "This setting affects only the resume question tail, not the answer body.",
  },
  {
    key: "controlledAnswerMode",
    label: "Controlled answer mode",
    help: "Default when a controlled answer card has no reply mode set.",
  },
  {
    key: "fieldObjectionMode",
    label: "Field objection mode",
  },
  {
    key: "factualKbAnswerMode",
    label: "Factual KB answer mode",
    help: "Default for factual answers from approved knowledge.",
  },
  {
    key: "trustAnswerMode",
    label: "Trust answer mode",
    help: "Default for trust, safety, or outcome concerns.",
  },
  {
    key: "unknownFallbackMode",
    label: "Unknown fallback mode",
    help: "Fallback when the AI cannot safely understand the message.",
  },
  {
    key: "smallTalkMode",
    label: "Small talk mode",
  },
  {
    key: "mediaCaptionMode",
    label: "Media caption mode",
  },
  {
    key: "reportMessageMode",
    label: "Report message mode",
  },
];

const SETTINGS_DRAFT_KEY = "settings";

const STEPS = [
  {
    id: "identity",
    title: "Basics",
    blurb: "Introduce your assistant and choose how it speaks to customers.",
    icon: User,
  },
  {
    id: "knowledge",
    title: "Reply rules",
    blurb: "Choose the information your assistant can use and how it handles missing answers.",
    icon: BookOpen,
  },
  {
    id: "qualification",
    title: "Lead collection settings",
    blurb: "Choose how your assistant collects enquiry details, then configure its questions.",
    icon: MessageSquare,
  },
  {
    id: "safety",
    title: "Handover & safety",
    blurb: "Set boundaries and explain when your team should take over.",
    icon: Shield,
  },
  {
    id: "autoreply",
    title: "Review & activation",
    blurb: "Review requirements, test the saved setup and choose when automatic replies are allowed.",
    icon: Send,
  },
];

function toText(value) {
  return String(value || "");
}

function toNumber(value, fallback) {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

function toOption(value, list, fallback) {
  const normalized = String(value || "").trim();
  return list.includes(normalized) ? normalized : fallback;
}

function normalizePlannerModeForDisplay(value) {
  if (value === "Primary") return "Live";
  if (value === "Live") return "Live";
  if (value === "Shadow" || value === "Off") return value;
  return DEFAULT_FORM.leadQualificationPlannerMode;
}

function normalizePlannerModeForSave(value) {
  if (value === "Live" || value === "Primary") return "Primary";
  if (value === "Shadow" || value === "Off") return value;
  return DEFAULT_FORM.leadQualificationPlannerMode;
}

function normalizeResponsePolicy(data) {
  const warnings = [];
  const policy = {};

  RESPONSE_POLICY_FIELDS.forEach(({ key }) => {
    const fallback = BALANCED_RESPONSE_POLICY[key];
    const rawValue = data?.[key];
    const normalized = toOption(rawValue, RESPONSE_POLICY_MODES, fallback);
    policy[key] = normalized;
    if (rawValue != null && normalized !== rawValue) {
      warnings.push(`${key} was reset to ${fallback}.`);
    }
  });

  return { policy, warnings };
}

const DEFAULT_LOCATION_PROOF_FALLBACK_POLICY =
  DEFAULT_FORM.locationProofFallbackPolicy;

function normalizeLocationProofFallbackPolicy(data, fallback = DEFAULT_LOCATION_PROOF_FALLBACK_POLICY) {
  const base = {
    enableLocationOnlyFallback: Boolean(
      fallback?.enableLocationOnlyFallback ?? true
    ),
    enableGenericFallback: Boolean(fallback?.enableGenericFallback ?? true),
    locationOnlyTemplate: toText(fallback?.locationOnlyTemplate),
    genericFallbackTemplate: toText(fallback?.genericFallbackTemplate),
  };

  if (!data) return base;

  const raw =
    typeof data === "string" ? parseLocationProofFallbackPolicyJson(data) : data;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return base;

  return {
    enableLocationOnlyFallback:
      raw.enableLocationOnlyFallback !== undefined
        ? Boolean(raw.enableLocationOnlyFallback)
        : base.enableLocationOnlyFallback,
    enableGenericFallback:
      raw.enableGenericFallback !== undefined
        ? Boolean(raw.enableGenericFallback)
        : base.enableGenericFallback,
    locationOnlyTemplate:
      raw.locationOnlyTemplate != null
        ? String(raw.locationOnlyTemplate)
        : base.locationOnlyTemplate,
    genericFallbackTemplate:
      raw.genericFallbackTemplate != null
        ? String(raw.genericFallbackTemplate)
        : base.genericFallbackTemplate,
  };
}

function parseLocationProofFallbackPolicyJson(raw) {
  if (!raw || !String(raw).trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function serializeLocationProofFallbackPolicy(policy) {
  const normalized = normalizeLocationProofFallbackPolicy(policy);
  return JSON.stringify({
    enableLocationOnlyFallback: Boolean(normalized.enableLocationOnlyFallback),
    enableGenericFallback: Boolean(normalized.enableGenericFallback),
    locationOnlyTemplate: String(normalized.locationOnlyTemplate || "").trim(),
    genericFallbackTemplate: String(normalized.genericFallbackTemplate || "").trim(),
  });
}

function toForm(data, fallbackLocationProofFallbackPolicy = DEFAULT_LOCATION_PROOF_FALLBACK_POLICY) {
  return {
    isEnabled: Boolean(data?.isEnabled),
    draftSuggestionEnabled: Boolean(data?.draftSuggestionEnabled),
    fallbackDryRunEnabled: Boolean(data?.fallbackDryRunEnabled),
    outsideFlowAiFallbackEnabled:
      data?.outsideFlowAiFallbackEnabled !== undefined
        ? Boolean(data.outsideFlowAiFallbackEnabled)
        : DEFAULT_FORM.outsideFlowAiFallbackEnabled,
    autoReplyAiRescueEnabled:
      data?.autoReplyAiRescueEnabled !== undefined
        ? Boolean(data.autoReplyAiRescueEnabled)
        : DEFAULT_FORM.autoReplyAiRescueEnabled,
    leadQualificationDryRunEnabled: Boolean(
      data?.leadQualificationDryRunEnabled
    ),
    leadQualificationAutoReplyMode: toOption(
      data?.leadQualificationAutoReplyMode,
      OPTIONS.leadQualificationAutoReplyMode,
      DEFAULT_FORM.leadQualificationAutoReplyMode
    ),
    leadQualificationPlannerMode: normalizePlannerModeForDisplay(
      data?.leadQualificationPlannerMode
    ),
    leadQualificationControlledAnswerLiveMode: toOption(
      data?.leadQualificationControlledAnswerLiveMode,
      OPTIONS.leadQualificationControlledAnswerLiveMode,
      DEFAULT_FORM.leadQualificationControlledAnswerLiveMode
    ),
    naturalReplyMode: OPTIONS.naturalReplyMode.includes(data?.naturalReplyMode)
      ? data.naturalReplyMode
      : null,
    aiAutoReplyMode: toOption(
      data?.aiAutoReplyMode,
      OPTIONS.aiAutoReplyMode,
      DEFAULT_FORM.aiAutoReplyMode
    ),
    requireSourceBackedAutoReply:
      data?.requireSourceBackedAutoReply !== undefined
        ? Boolean(data.requireSourceBackedAutoReply)
        : DEFAULT_FORM.requireSourceBackedAutoReply,
    maxAiAutoRepliesPerThread: toNumber(
      data?.maxAiAutoRepliesPerThread,
      DEFAULT_FORM.maxAiAutoRepliesPerThread
    ),
    maxAiAutoRepliesPerContactPerDay: toNumber(
      data?.maxAiAutoRepliesPerContactPerDay,
      DEFAULT_FORM.maxAiAutoRepliesPerContactPerDay
    ),
    businessHoursOnly: Boolean(data?.businessHoursOnly),
    handoverOnNoSource:
      data?.handoverOnNoSource !== undefined
        ? Boolean(data.handoverOnNoSource)
        : DEFAULT_FORM.handoverOnNoSource,
    safeNoSourceFallbackEnabled: Boolean(data?.safeNoSourceFallbackEnabled),
    approvedMediaAutoSendEnabled: Boolean(data?.approvedMediaAutoSendEnabled),
    maxSafeNoSourceFallbacksPerThread: toNumber(
      data?.maxSafeNoSourceFallbacksPerThread,
      DEFAULT_FORM.maxSafeNoSourceFallbacksPerThread
    ),
    handoverOnSensitiveTopic:
      data?.handoverOnSensitiveTopic !== undefined
        ? Boolean(data.handoverOnSensitiveTopic)
        : DEFAULT_FORM.handoverOnSensitiveTopic,
    botDisplayName: toText(data?.botDisplayName || DEFAULT_FORM.botDisplayName),
    botRoleName: toText(data?.botRoleName || DEFAULT_FORM.botRoleName),
    disclosureMode: toOption(
      data?.disclosureMode,
      OPTIONS.disclosureMode,
      DEFAULT_FORM.disclosureMode
    ),
    greetingTemplate: toText(data?.greetingTemplate),
    industryType: toOption(
      data?.industryType,
      OPTIONS.industryType,
      DEFAULT_FORM.industryType
    ),
    businessGoal: toText(data?.businessGoal),
    languageMode: toOption(
      data?.languageMode,
      OPTIONS.languageMode,
      DEFAULT_FORM.languageMode
    ),
    tone: toOption(data?.tone, OPTIONS.tone, DEFAULT_FORM.tone),
    replyLength: toOption(
      data?.replyLength,
      OPTIONS.replyLength,
      DEFAULT_FORM.replyLength
    ),
    emojiUsage: toOption(
      data?.emojiUsage,
      OPTIONS.emojiUsage,
      DEFAULT_FORM.emojiUsage
    ),
    requireSourceBackedAnswer:
      data?.requireSourceBackedAnswer !== undefined
        ? Boolean(data.requireSourceBackedAnswer)
        : DEFAULT_FORM.requireSourceBackedAnswer,
    allowGeneralKnowledgeFallback: Boolean(data?.allowGeneralKnowledgeFallback),
    maxRetrievedChunks: toNumber(data?.maxRetrievedChunks, 5),
    noSourceBehavior: toOption(
      data?.noSourceBehavior,
      OPTIONS.noSourceBehavior,
      DEFAULT_FORM.noSourceBehavior
    ),
    allowedTopicsNotes: toText(data?.allowedTopicsNotes),
    blockedTopicsNotes: toText(data?.blockedTopicsNotes),
    sensitiveTopicPolicy: toOption(
      data?.sensitiveTopicPolicy,
      OPTIONS.sensitiveTopicPolicy,
      DEFAULT_FORM.sensitiveTopicPolicy
    ),
    escalationRules: toText(data?.escalationRules),
    handoverMessage: toText(data?.handoverMessage || DEFAULT_FORM.handoverMessage),
    photoReceivedMessage: toText(data?.photoReceivedMessage),
    leadSessionTimeoutHours:
      data?.leadSessionTimeoutHours != null
        ? Number(data.leadSessionTimeoutHours)
        : null,
    leadRestartPolicy: data?.leadRestartPolicy ?? "AdminOnly",
    maxAiRepliesPerThread: toNumber(data?.maxAiRepliesPerThread, 5),
    maxAiRepliesPerContactPerDay: toNumber(
      data?.maxAiRepliesPerContactPerDay,
      10
    ),
    maxOutputTokens: toNumber(data?.maxOutputTokens, 300),
    providerTimeoutSeconds: toNumber(data?.providerTimeoutSeconds, 15),
    maxContextMessages: toNumber(data?.maxContextMessages, 20),
    temperature: toNumber(data?.temperature, 0.3),
    additionalInstructions: toText(
      data?.additionalInstructions || data?.systemInstruction
    ),
    locationProofFallbackPolicy: normalizeLocationProofFallbackPolicy(
      data?.locationProofFallbackPolicyJson,
      fallbackLocationProofFallbackPolicy
    ),
  };
}

function fieldValue(value) {
  const trimmed = String(value || "").trim();
  return trimmed.length ? trimmed : null;
}

function FieldRow({ label, help, htmlFor, children }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="block text-sm font-medium text-slate-800"
      >
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {help ? (
        <p className="mt-1 text-xs text-slate-500">{help}</p>
      ) : null}
    </div>
  );
}

function TextField({ label, help, name, value, onChange, disabled, maxLength, placeholder }) {
  const id = `field-${name}`;
  return (
    <FieldRow label={label} help={help} htmlFor={id}>
      <input
        id={id}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={event => onChange(name, event.target.value)}
        disabled={disabled}
        className="h-10 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </FieldRow>
  );
}

function TextAreaField({ label, help, name, value, onChange, disabled, rows = 3, placeholder }) {
  const id = `field-${name}`;
  return (
    <FieldRow label={label} help={help} htmlFor={id}>
      <textarea
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={event => onChange(name, event.target.value)}
        disabled={disabled}
        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </FieldRow>
  );
}

function NumberField({ label, help, name, value, onChange, disabled, min, max, step }) {
  const id = `field-${name}`;
  return (
    <FieldRow label={label} help={help} htmlFor={id}>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step || 1}
        value={value}
        onChange={event => onChange(name, event.target.value)}
        disabled={disabled}
        className="h-10 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </FieldRow>
  );
}

function SelectField({ label, help, name, value, options, onChange, disabled, labels }) {
  const id = `field-${name}`;
  return (
    <FieldRow label={label} help={help} htmlFor={id}>
      <select
        id={id}
        value={value}
        onChange={event => onChange(name, event.target.value)}
        disabled={disabled}
        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-50 disabled:text-slate-500"
      >
        {options.map(option => (
          <option key={option} value={option}>
            {labels?.[option] || option}
          </option>
        ))}
      </select>
    </FieldRow>
  );
}

function ToggleRow({ label, help, name, value, onChange, disabled }) {
  const id = `field-${name}`;
  return (
    <label
      htmlFor={id}
      className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:bg-slate-50"
    >
      <input
        id={id}
        type="checkbox"
        checked={Boolean(value)}
        onChange={event => onChange(name, event.target.checked)}
        disabled={disabled}
        className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
      />
      <span className="text-sm">
        <span className="block font-medium text-slate-800">{label}</span>
        {help ? (
          <span className="mt-0.5 block text-xs font-normal text-slate-500">
            {help}
          </span>
        ) : null}
      </span>
    </label>
  );
}

function Disclosure({ title, hint, defaultOpen = false, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const panelId = React.useId();
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          {open ? (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-500" />
          )}
          {title}
        </span>
        {hint && !open ? (
          <span className="text-xs text-slate-500">{hint}</span>
        ) : null}
      </button>
      {open ? <div id={panelId} className="border-t border-slate-100 p-4">{children}</div> : null}
    </div>
  );
}

function HelpCard({ tone = "info", title, children }) {
  const palette = {
    info: "border-slate-200 bg-slate-50 text-slate-600",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  }[tone];
  const Icon = tone === "warn" ? AlertTriangle : tone === "success" ? CheckCircle2 : Info;
  return (
    <div className={`flex gap-2 rounded-lg border px-3 py-2.5 text-xs ${palette}`}>
      <Icon className="mt-0.5 h-4 w-4 flex-none" />
      <div className="space-y-1">
        {title ? <div className="font-medium">{title}</div> : null}
        <div className="space-y-1">{children}</div>
      </div>
    </div>
  );
}

function Stepper({ steps, currentId, visitedIds, onSelect, disabled }) {
  return (
    <nav aria-label="AI setup sections">
      <ol className="space-y-1">
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isCurrent = step.id === currentId;
          const isVisited = visitedIds.has(step.id);
          return <li key={step.id}>
            <button type="button" onClick={() => onSelect(step.id)} disabled={disabled}
              aria-current={isCurrent ? "step" : undefined}
              aria-label={`Step ${index + 1} ${step.title}${isVisited ? " Visited" : ""}`}
              className={`flex min-h-11 w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-left text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:opacity-50 ${isCurrent ? "bg-slate-200/80 font-medium text-slate-950" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
              <StepIcon aria-hidden="true" className={`h-4 w-4 flex-none ${isCurrent ? "text-emerald-700" : "text-slate-400"}`} />
              <span>{step.title}</span>
            </button>
          </li>;
        })}
      </ol>
    </nav>
  );
}

function WorkspaceLinks({ auth, itemKeys, label }) {
  const labels = {
    ai_agent_knowledge_base: "Manage knowledge", ai_agent_media_assets: "Approved media",
    ai_agent_lead_qualification: "Manage questions & follow-ups", ai_agent_test_console: "Test saved setup",
    ai_agent_usage: "Usage & activity", ai_agent_lead_qualification_debug: "Advanced diagnostics",
  };
  const items = itemKeys.map(key => AI_AGENT_SUBMENU_ITEMS.find(item => item.key === key)).filter(item => item
    && (auth.hasAllAccess || (typeof auth.can === "function" && (item.requireAllPermissions
      ? item.permissionCodes.every(code => auth.can(code))
      : item.permissionCodes.some(code => auth.can(code))))));
  if (!items.length) return null;
  return <nav aria-label={label} className="flex flex-wrap gap-2">
    {items.map(item => <Link key={item.key} to={item.path}
      className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600">
      {labels[item.key] || item.label}<ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
    </Link>)}
  </nav>;
}

function StepHeader({ step, headingRef }) {
  const Icon = step.icon;
  return (
    <div className="flex items-start gap-3 border-b border-slate-100 pb-4">
      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-slate-100 text-emerald-700">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <h2 ref={headingRef} tabIndex={-1} className="scroll-mt-24 text-base font-semibold text-slate-900 focus:outline-none">{step.title}</h2>
        <p className="mt-0.5 text-sm text-slate-600">{step.blurb}</p>
      </div>
    </div>
  );
}

function IdentityStep({ form, onChange, canManage }) {
  return (
    <div className="space-y-5">
      <ToggleRow
        label="Enable AI Agent for this business"
        help="When disabled, AI stops new work and queued replies. Messages already submitted to WhatsApp may still finish. Re-enabling does not replay stopped jobs."
        name="isEnabled"
        value={form.isEnabled}
        onChange={onChange}
        disabled={!canManage}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextField
          label="Bot display name"
          help="Name customers see when the AI introduces itself."
          name="botDisplayName"
          value={form.botDisplayName}
          onChange={onChange}
          disabled={!canManage}
          maxLength={80}
        />
        <TextField
          label="Bot role name"
          help="Describes the AI's role to customers."
          name="botRoleName"
          value={form.botRoleName}
          onChange={onChange}
          disabled={!canManage}
          maxLength={80}
        />
      </div>

      <SelectField
        label="AI introduction"
        name="disclosureMode"
        value={form.disclosureMode}
        options={OPTIONS.disclosureMode}
        labels={OPTION_LABELS.disclosureMode}
        onChange={onChange}
        disabled={!canManage}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SelectField
          label="Industry type"
          name="industryType"
          value={form.industryType}
          options={OPTIONS.industryType}
          labels={OPTION_LABELS.industryType}
          onChange={onChange}
          disabled={!canManage}
        />
        <TextField
          label="Greeting message (Optional)"
          help="Sent when the AI starts a conversation."
          name="greetingTemplate"
          value={form.greetingTemplate}
          onChange={onChange}
          disabled={!canManage}
          maxLength={500}
        />
      </div>

      <TextAreaField
        label="Business goal"
        help="Describe what you want the AI to accomplish."
        name="businessGoal"
        value={form.businessGoal}
        onChange={onChange}
        disabled={!canManage}
        placeholder="Example: Answer product questions and collect requirements for our sales team."
        rows={3}
      />

      <ToggleRow
        label="Enable AI draft suggestions in Chat Inbox"
        help="Your team sees suggested replies, but the AI does not send them."
        name="draftSuggestionEnabled"
        value={form.draftSuggestionEnabled}
        onChange={onChange}
        disabled={!canManage}
      />
    </div>
  );
}

function VoiceStep({ form, onChange, canManage }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SelectField
          label="Language"
          help="Auto-detect picks the customer's own language when possible."
          name="languageMode"
          value={form.languageMode}
          options={OPTIONS.languageMode}
          labels={OPTION_LABELS.languageMode}
          onChange={onChange}
          disabled={!canManage}
        />
        <SelectField
          label="Tone"
          name="tone"
          value={form.tone}
          options={OPTIONS.tone}
          labels={OPTION_LABELS.tone}
          onChange={onChange}
          disabled={!canManage}
        />
        <SelectField
          label="Reply length"
          help="Short works best for WhatsApp. Detailed fits product or policy questions."
          name="replyLength"
          value={form.replyLength}
          options={OPTIONS.replyLength}
          labels={OPTION_LABELS.replyLength}
          onChange={onChange}
          disabled={!canManage}
        />
        <SelectField
          label="Emoji usage"
          name="emojiUsage"
          value={form.emojiUsage}
          options={OPTIONS.emojiUsage}
          labels={OPTION_LABELS.emojiUsage}
          onChange={onChange}
          disabled={!canManage}
        />
      </div>
    </div>
  );
}

function KnowledgeStep({ form, onChange, canManage }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">Manage business information in the Knowledge Base. The controls below set answer boundaries and fallback behaviour.</p>

      <ToggleRow
        label="Only answer from approved knowledge sources"
        help="Recommended for regulated businesses. The AI will not answer without an approved source."
        name="requireSourceBackedAnswer"
        value={form.requireSourceBackedAnswer}
        onChange={onChange}
        disabled={!canManage}
      />

      <ToggleRow
        label="Allow general fallback for non-business questions"
        help="Allows polite replies to small talk or general questions."
        name="allowGeneralKnowledgeFallback"
        value={form.allowGeneralKnowledgeFallback}
        onChange={onChange}
        disabled={!canManage}
      />

      <SelectField
        label="When the AI cannot find an approved source"
        name="noSourceBehavior"
        value={form.noSourceBehavior}
        options={OPTIONS.noSourceBehavior}
        labels={OPTION_LABELS.noSourceBehavior}
        onChange={onChange}
        disabled={!canManage}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextAreaField
          label="Topics this AI may discuss"
          name="allowedTopicsNotes"
          value={form.allowedTopicsNotes}
          onChange={onChange}
          disabled={!canManage}
        />
        <TextAreaField
          label="Topics this AI must avoid"
          help="Use Handover & safety for escalation rules."
          name="blockedTopicsNotes"
          value={form.blockedTopicsNotes}
          onChange={onChange}
          disabled={!canManage}
        />
      </div>

      <Disclosure
        title="Advanced retrieval"
        hint="Tune how many knowledge chunks are retrieved"
      >
        <NumberField
          label="Max retrieved chunks"
          help="How many knowledge snippets to use per answer. 3-5 is a good default."
          name="maxRetrievedChunks"
          value={form.maxRetrievedChunks}
          min={1}
          max={10}
          onChange={onChange}
          disabled={!canManage}
        />
      </Disclosure>
    </div>
  );
}

function SafetyStep({ form, onChange, canManage }) {
  return (
    <div className="space-y-5">
      <p className="flex items-start gap-2 text-xs text-slate-500"><Info aria-hidden="true" className="h-4 w-4 flex-none" />Safety checks still apply to custom instructions and handover rules.</p>

      <SelectField
        label="Sensitive topic policy"
        help="Choose what happens for medical, legal, refund, or other sensitive questions."
        name="sensitiveTopicPolicy"
        value={form.sensitiveTopicPolicy}
        options={OPTIONS.sensitiveTopicPolicy}
        labels={OPTION_LABELS.sensitiveTopicPolicy}
        onChange={onChange}
        disabled={!canManage}
      />

      <ToggleRow
        label="Recommend handover for sensitive topics"
        help="Suggest team takeover when the AI detects a sensitive topic."
        name="handoverOnSensitiveTopic"
        value={form.handoverOnSensitiveTopic}
        onChange={onChange}
        disabled={!canManage}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextAreaField
          label="Escalation rules"
          help="Example: Always hand over when the customer mentions a refund."
          name="escalationRules"
          value={form.escalationRules}
          onChange={onChange}
          disabled={!canManage}
          rows={4}
        />
        <TextAreaField
          label="Handover message"
          help="Sent when the AI hands over to your team."
          name="handoverMessage"
          value={form.handoverMessage}
          onChange={onChange}
          disabled={!canManage}
          rows={4}
        />
      </div>

      <TextAreaField
        label="Photo received message"
        help="Sent after a customer shares a photo. Leave blank to use the default."
        name="photoReceivedMessage"
        value={form.photoReceivedMessage}
        onChange={onChange}
        disabled={!canManage}
        rows={3}
        placeholder="Photo received. Our team will review and update you on the next steps."
      />

      <TextAreaField
        label="Additional instructions"
        help="Extra guidance for the AI. Safety rules still apply."
        name="additionalInstructions"
        value={form.additionalInstructions}
        onChange={onChange}
        disabled={!canManage}
        rows={5}
        placeholder="Example: Ask our team to handle pricing exceptions and complaints."
      />
    </div>
  );
}

function LeadCollectionStep({ form, onChange, canManage, canManageAutoReply, auth }) {
  const changeQualification = value => {
    onChange("leadQualificationDryRunEnabled", value !== "Off");
    onChange("leadQualificationAutoReplyMode", value);
    if (value !== "InternalBeta") {
      if (form.leadQualificationPlannerMode === "Live") onChange("leadQualificationPlannerMode", value === "Off" ? "Off" : "Shadow");
      if (form.leadQualificationControlledAnswerLiveMode === "LiveForSelectedScenarios") onChange("leadQualificationControlledAnswerLiveMode", value === "Off" ? "Off" : "Shadow");
    }
  };
  return <div className="space-y-5">
    <WorkspaceLinks auth={auth} itemKeys={["ai_agent_lead_qualification"]} label="Lead question configuration" />
    <p className="text-xs text-slate-500">Questions, required details and confirmation are managed in Lead collection. Save pending settings before opening another page.</p>
    <section aria-label="Lead collection capabilities" className="space-y-4">
        <label className="block text-sm">Collect lead details
          <select value={!form.leadQualificationDryRunEnabled ? "Off" : form.leadQualificationAutoReplyMode === "InternalBeta" ? "InternalBeta" : "DryRunOnly"}
            onChange={event => changeQualification(event.target.value)} disabled={!canManage || !canManageAutoReply} className="mt-1 block w-full rounded border p-2">
            <option value="Off">Off</option><option value="DryRunOnly">Evaluate without customer replies</option><option value="InternalBeta">Controlled test replies (requires rollout)</option>
          </select>
        </label>
        <p className="text-xs text-slate-600">Switching qualification to evaluation or off also changes any live qualification planner and controlled qualification sending to evaluation or off in this draft. Review and save to apply it.</p>
        <label className="block text-sm">Send configured qualification answers
          <select value={form.leadQualificationControlledAnswerLiveMode} onChange={event => onChange("leadQualificationControlledAnswerLiveMode", event.target.value)} disabled={!canManage || !canManageAutoReply} className="mt-1 block w-full rounded border p-2">
            <option value="Off">Off</option><option value="Shadow">Evaluate without sending</option><option value="LiveForSelectedScenarios">Send for approved scenarios when eligible</option>
          </select>
        </label>
    </section>
    <Disclosure title="Session & restart settings" hint="When an enquiry expires or can start again">
      <div className="space-y-4">
        <FieldRow
          label="Lead session timeout hours"
          help="After this many inactive hours, a new enquiry can start. Blank uses 24 hours."
          htmlFor="leadSessionTimeoutHours"
        >
          <input
            id="leadSessionTimeoutHours"
            type="number"
            min={1}
            max={168}
            placeholder="24"
            value={form.leadSessionTimeoutHours ?? ""}
            onChange={event =>
              onChange(
                "leadSessionTimeoutHours",
                event.target.value === ""
                  ? null
                  : Number(event.target.value)
              )
            }
            disabled={!canManage}
            className="mt-1 w-32 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </FieldRow>

        <SelectField
          label="Lead restart policy"
          help="Controls whether customers can restart after their details are saved."
          name="leadRestartPolicy"
          value={form.leadRestartPolicy}
          options={OPTIONS.leadRestartPolicy}
          labels={OPTION_LABELS.leadRestartPolicy}
          onChange={onChange}
          disabled={!canManage}
        />

      </div>
    </Disclosure>
    <p className="flex items-start gap-2 text-xs text-slate-500"><Info aria-hidden="true" className="h-4 w-4 flex-none" />Changes stay in your browser draft until saved. Sending activation requires confirmation. Advanced qualification modes remain in Review & activation.</p>
  </div>;
}

function AutoReplyStep({
  form,
  onChange,
  onLocationProofFallbackPolicyChange,
  responsePolicy,
  onResponsePolicyChange,
  setResponsePolicy,
  setResponsePolicyWarnings,
  responsePolicyWarnings,
  responsePolicySaving,
  onSaveResponsePolicy,
  canManage,
  canManageAutoReply,
  autoReplyChecklist,
  safeOnlySelectable,
  safeOnlyDisabledReason,
  internalBetaSelectable,
  internalBetaDisabledReason,
  mediaAutoSendRolloutKnown,
  mediaAutoSendPlatformEnabled,
  mediaAutoSendDisabledReason,
  videoAutoSendPlatformEnabled,
  videoAutoSendDisabledReason,
  dependencyErrors,
}) {
  return (
    <div className="space-y-6">
      <p className="flex items-start gap-2 text-xs text-slate-500"><Info aria-hidden="true" className="h-4 w-4 flex-none" />Tests use the saved setup. Save changes before testing; newly enabled customer messaging requires confirmation.</p>

      <section aria-label="Core business capabilities" className="space-y-4 rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-medium text-slate-900">Answer business questions</h3>
        <p className="text-sm text-slate-600">Staff suggestions are configured in Basics; enquiry collection in Lead collection settings. Choose automatic knowledge answers here. Saving preserves custom settings and asks you to confirm new sending activation.</p>
        <label className="block text-sm">Send approved knowledge answers
          <select value={form.aiAutoReplyMode} onChange={event => onChange("aiAutoReplyMode", event.target.value)} disabled={!canManage || !canManageAutoReply} className="mt-1 block w-full rounded border p-2">
            <option value="Off">Off</option><option value="DryRunOnly">Evaluate without sending</option><option value="AutoReplySafeOnly" disabled={!safeOnlySelectable}>Send safe approved answers when eligible</option>
            {form.aiAutoReplyMode === "InternalBeta" ? <option value="InternalBeta">Existing controlled test sending (operator)</option> : null}
          </select>
        </label>
        {dependencyErrors.length ? <ul aria-label="Capability requirements" className="list-disc pl-5 text-sm text-red-700">{dependencyErrors.map(item => <li key={item.field}>{item.message}</li>)}</ul> : null}

      </section>
      <div className="space-y-3">
        <details className="rounded-lg border border-slate-200 p-3"><summary className="cursor-pointer font-medium">Advanced qualification modes</summary><div className="mt-4 space-y-4">
        <ToggleRow
          label="Enable Lead Qualification Dry-Run"
          help="Evaluates the lead flow. Sending is controlled separately by the qualification and approved-answer modes below."
          name="leadQualificationDryRunEnabled"
          value={form.leadQualificationDryRunEnabled}
          onChange={onChange}
          disabled={!canManage || (!canManageAutoReply && anyLive(form))}
        />

        <SelectField
          label="Lead Qualification Auto-Reply Mode"
          help="Controls whether lead qualification can send controlled test replies."
          name="leadQualificationAutoReplyMode"
          value={form.leadQualificationAutoReplyMode}
          options={OPTIONS.leadQualificationAutoReplyMode}
          labels={OPTION_LABELS.leadQualificationAutoReplyMode}
          onChange={onChange}
          disabled={!canManage || !canManageAutoReply}
        />
        {form.leadQualificationAutoReplyMode === "InternalBeta" ? (
          <HelpCard tone="warn">
            Internal beta can send lead qualification messages when all checks
            pass. Use it only with controlled test senders first.
          </HelpCard>
        ) : null}

        <SelectField
          label="Lead Qualification Planner Mode"
          help="Shadow logs decisions only. Live uses planner decisions in the lead flow."
          name="leadQualificationPlannerMode"
          value={form.leadQualificationPlannerMode}
          options={OPTIONS.leadQualificationPlannerMode}
          labels={OPTION_LABELS.leadQualificationPlannerMode}
          onChange={onChange}
          disabled={!canManage || !canManageAutoReply}
        />


        <SelectField
          label="Controlled Answer Live Mode"
          help="Controls when approved lead qualification replies can be sent live."
          name="leadQualificationControlledAnswerLiveMode"
          value={form.leadQualificationControlledAnswerLiveMode}
          options={OPTIONS.leadQualificationControlledAnswerLiveMode}
          labels={CONTROLLED_ANSWER_LIVE_MODE_LABELS}
          onChange={onChange}
          disabled={!canManage || !canManageAutoReply}
        />
        {form.leadQualificationControlledAnswerLiveMode ===
        "LiveForSelectedScenarios" ? (
          <HelpCard tone="warn">
            This mode sends only configured controlled replies for selected
            lead qualification scenarios. Repeated refusal or escalation still
            uses the handover message.
          </HelpCard>
        ) : null}

        </div></details>
        <div className="grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700">
            <span className="font-medium">Media rollout:</span>{" "}
            {mediaAutoSendRolloutKnown
              ? mediaAutoSendPlatformEnabled
                ? "Enabled"
                : "Disabled"
              : "Unknown"}
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700">
            <span className="font-medium">Video rollout:</span>{" "}
            {mediaAutoSendRolloutKnown
              ? videoAutoSendPlatformEnabled
                ? "Enabled"
                : "Disabled"
              : "Unknown"}
            {!videoAutoSendPlatformEnabled && mediaAutoSendRolloutKnown ? (
              <span className="mt-1 block text-amber-700">
                {videoAutoSendDisabledReason}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <Disclosure
        title="Advanced: Response Composition Policy"
        hint="Business defaults for controlled answer, knowledge, fallback, and media reply styles"
      >
        {responsePolicyWarnings.length > 0 ? (
          <HelpCard tone="warn">
            {responsePolicyWarnings[0]}
          </HelpCard>
        ) : (
          <HelpCard tone="info">
            Controlled Answer Mode is policy-level only and may use
            UseCardSetting. Controlled Answer Cards themselves still support
            only Configured and AiPolished. AiPolished policy does not mean
            AiPolished controlled replies are live-enabled.
          </HelpCard>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {RESPONSE_POLICY_FIELDS.map(field => (
            <div key={field.key}>
              <SelectField
                label={field.label}
                help={field.help}
                name={field.key}
                value={responsePolicy[field.key]}
                options={RESPONSE_POLICY_MODES}
                labels={
                  field.key === "resumeQuestionMode"
                    ? RESUME_QUESTION_MODE_LABELS
                    : RESPONSE_POLICY_MODE_LABELS
                }
                onChange={onResponsePolicyChange}
                disabled={!canManage || responsePolicySaving}
              />
              {field.note ? (
                <p className="mt-1 px-1 text-xs text-slate-500">{field.note}</p>
              ) : null}
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-2 text-xs text-slate-600">
          <p>
            {`Configured uses the configured resume question text exactly as written.`}
          </p>
          <p>
            {`AI-polished may improve only the wording of the resume question. The same field must still be asked. If validation fails, the configured text is sent.`}
          </p>
          <p>
            {`Live AI-polished resume questions require Natural Reply Mode = Live. Shadow and DryRun only preview or log the polished resume tail.`}
          </p>
          <p>
            {`This setting only affects the resume question tail. Controlled answer card AI-polished live sending is controlled separately and remains disabled unless explicitly enabled elsewhere.`}
          </p>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setResponsePolicy(BALANCED_RESPONSE_POLICY);
              setResponsePolicyWarnings([]);
            }}
            disabled={!canManage || responsePolicySaving}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset to Balanced defaults
          </button>
          <button
            type="button"
            onClick={onSaveResponsePolicy}
            disabled={!canManage || responsePolicySaving}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${
              !canManage || responsePolicySaving
                ? "cursor-not-allowed bg-slate-200 text-slate-500"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
            }`}
          >
            <Save className="h-4 w-4" />
            {responsePolicySaving ? "Saving..." : "Save Response Policy"}
          </button>
          <p className="text-xs text-slate-500">Saves this policy together with all other edited setup settings.</p>
        </div>
      </Disclosure>

      <Disclosure
        title="Location Proof Fallback Wording"
        hint="Intro wording when location proof data is incomplete"
      >
        <HelpCard tone="info">
          This controls intro wording when location proof data is incomplete.
          Media sending remains controlled by existing media trigger rules.
        </HelpCard>

        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <div className="font-medium text-slate-800">
              Supported placeholders
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {[
                "{CustomerName}",
                "{LocationName}",
                "{Location}",
                "{NearbyAreas}",
                "{Disclaimer}",
              ].map(token => (
                <span
                  key={token}
                  className="rounded-full border border-slate-200 bg-white px-2 py-0.5 font-mono text-[11px] text-slate-700"
                >
                  {token}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <ToggleRow
              label="Enable location-only fallback"
              name="enableLocationOnlyFallback"
              value={form.locationProofFallbackPolicy.enableLocationOnlyFallback}
              onChange={onLocationProofFallbackPolicyChange}
              disabled={!canManage}
            />
            <TextAreaField
              label="Location-only fallback template"
              help="Used when a location proof row exists but nearby areas are empty."
              name="locationOnlyTemplate"
              value={form.locationProofFallbackPolicy.locationOnlyTemplate}
              onChange={onLocationProofFallbackPolicyChange}
              disabled={!canManage}
              rows={4}
            />
            <ToggleRow
              label="Enable generic fallback"
              name="enableGenericFallback"
              value={form.locationProofFallbackPolicy.enableGenericFallback}
              onChange={onLocationProofFallbackPolicyChange}
              disabled={!canManage}
            />
            <TextAreaField
              label="Generic fallback template"
              help="Used when no matching location proof row is found."
              name="genericFallbackTemplate"
              value={form.locationProofFallbackPolicy.genericFallbackTemplate}
              onChange={onLocationProofFallbackPolicyChange}
              disabled={!canManage}
              rows={4}
              placeholder="{CustomerName} ji, {LocationName} area ke liye relevant approved information available hai. Main aapko details share kar raha/rahi hoon."
            />
          </div>
        </div>
      </Disclosure>

      {canManageAutoReply ? (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">
            Send approved answers
          </h3>
          <HelpCard tone="warn">
            Controlled rollout. AI may send WhatsApp replies only when an
            approved source and safety checks pass.
          </HelpCard>

          <details className="rounded-lg border p-3"><summary>Advanced sending modes</summary>
          <label className="block text-sm">
            <span className="block font-medium text-slate-800">
              Auto-reply mode
            </span>
            <select
              aria-label="Auto-reply mode"
              value={form.aiAutoReplyMode}
              onChange={event => onChange("aiAutoReplyMode", event.target.value)}
              disabled={!canManage}
              className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            >
              {OPTIONS.aiAutoReplyMode.map(option => {
                const disabled =
                  (option === "InternalBeta" && !internalBetaSelectable) ||
                  (option === "AutoReplySafeOnly" && !safeOnlySelectable);
                const title = disabled
                  ? option === "InternalBeta"
                    ? internalBetaDisabledReason
                    : safeOnlyDisabledReason
                  : undefined;
                return (
                  <option
                    key={option}
                    value={option}
                    disabled={disabled}
                    title={title}
                  >
                    {OPTION_LABELS.aiAutoReplyMode[option]}
                    {disabled ? " - Not available" : ""}
                  </option>
                );
              })}
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              Start with Off or dry-run. SafeOnly unlocks after readiness checks pass.
            </span>
            {!safeOnlySelectable ? (
              <span className="mt-1 block text-xs text-amber-700">
                SafeOnly locked: {safeOnlyDisabledReason}
              </span>
            ) : null}
          </label>
          </details>

          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <h4 className="text-sm font-semibold text-slate-900">
              AI Auto Reply Flow Control
            </h4>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Outside AutoReply Flow
            </p>
            <ToggleRow
              label="AI replies outside AutoReply flow"
              help="When no flow is active, AI can answer using approved knowledge."
              name="outsideFlowAiFallbackEnabled"
              value={form.outsideFlowAiFallbackEnabled}
              onChange={onChange}
              disabled={!canManage || !form.isEnabled}
            />
            <p className="pt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Inside AutoReply Flow
            </p>
            <ToggleRow
              label="AI Rescue inside AutoReply flows"
              help="Lets selected flows use AI when a customer's message does not match the flow."
              name="autoReplyAiRescueEnabled"
              value={form.autoReplyAiRescueEnabled}
              onChange={onChange}
              disabled={!canManage || !form.isEnabled}
            />
            <p className="text-xs text-slate-500">
              Path: AutoReply → Flow Builder → Open flow → Recovery Options
            </p>
          </div>

          <div className="space-y-3">
            <ToggleRow
              label="Require source-backed auto-reply"
              help="Auto-replies only send when backed by approved knowledge."
              name="requireSourceBackedAutoReply"
              value={form.requireSourceBackedAutoReply}
              onChange={onChange}
              disabled={!canManage}
            />
            <ToggleRow
              label="Business hours only"
              help="Unavailable: business-hours scheduling is not implemented. A previously enabled value can be turned off."
              name="businessHoursOnly"
              value={form.businessHoursOnly}
              onChange={onChange}
              disabled={!canManage || !form.businessHoursOnly}
            />

            <ToggleRow
              label="Recommend handover when no source is found"
              help="Suggest team takeover when no approved source matches."
              name="handoverOnNoSource"
              value={form.handoverOnNoSource}
              onChange={onChange}
              disabled={!canManage}
            />
            <ToggleRow
              label="Send safe no-source fallback"
              help="Sends a fixed safety message instead of inventing an answer."
              name="safeNoSourceFallbackEnabled"
              value={form.safeNoSourceFallbackEnabled}
              onChange={onChange}
              disabled={!canManage}
            />
            <ToggleRow
              label="Approved Media Auto-Send"
              help="Allow the AI to send matching approved images or PDFs."
              name="approvedMediaAutoSendEnabled"
              value={form.approvedMediaAutoSendEnabled}
              onChange={onChange}
              disabled={!canManage}
            />
            {mediaAutoSendRolloutKnown && !mediaAutoSendPlatformEnabled ? (
              <HelpCard tone="warn">{mediaAutoSendDisabledReason}</HelpCard>
            ) : null}

            <NumberField
              label="Max safe no-source fallbacks per thread"
              help="Limit fixed fallback messages in one thread. Recommended: 1-2."
              name="maxSafeNoSourceFallbacksPerThread"
              value={form.maxSafeNoSourceFallbacksPerThread}
              min={0}
              max={3}
              onChange={onChange}
              disabled={!canManage}
            />
          </div>

          {autoReplyChecklist.length > 0 ? (
            <details className="rounded-lg border border-slate-200 bg-slate-50 p-3"><summary className="cursor-pointer text-sm font-medium text-slate-700">Detailed knowledge sending checks</summary>
            <div className="mt-3">
              <div className="mb-2 text-xs font-semibold text-slate-700">
                SafeOnly readiness checklist
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {autoReplyChecklist.map(item => (
                  <div
                    key={item.key}
                    className={`rounded-md border px-2.5 py-2 text-xs ${
                      item.passed
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-amber-200 bg-amber-50 text-amber-800"
                    }`}
                  >
                    <div className="font-medium">
                      {item.passed === true ? "Ready" : item.passed === false ? "Blocked" : "Unknown"}: {item.label}
                    </div>
                    {!item.passed && item.message ? (
                      <div className="mt-0.5 font-normal">{item.message}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div></details>
          ) : null}
        </div>
      ) : null}

      <ToggleRow
        label="Enable AI Fallback Dry-Run"
        help="Checks missed automation messages without sending anything."
        name="fallbackDryRunEnabled"
        value={form.fallbackDryRunEnabled}
        onChange={onChange}
        disabled={!canManage || (!canManageAutoReply && anyLive(form))}
      />

      <Disclosure title="Advanced wording and diagnostics" hint="Optional rephrasing modes; keep current custom values unless you intend to change them."><div className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Natural reply</h3>
        <HelpCard tone="info">
          Natural reply can make approved replies sound more human. Shadow logs
          changes, dry-run previews them, and Live sends them.
        </HelpCard>
        <SelectField
          label="Natural Reply Mode"
          help="Platform default uses the account-level setting."
          name="naturalReplyMode"
          value={form.naturalReplyMode ?? ""}
          options={["", ...OPTIONS.naturalReplyMode]}
          labels={{ "": "Platform default", ...OPTION_LABELS.naturalReplyMode }}
          onChange={(name, val) => onChange(name, val === "" ? null : val)}
          disabled={!canManage || !canManageAutoReply}
        />
      </div></Disclosure>

      <Disclosure
        title="Advanced runtime limits"
        hint="Replies per thread, tokens, timeouts, temperature"
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <NumberField
            label="Max AI replies per thread"
            help="Conversation cap, including drafts."
            name="maxAiRepliesPerThread"
            value={form.maxAiRepliesPerThread}
            min={0}
            max={100}
            onChange={onChange}
            disabled={!canManage}
          />
          <NumberField
            label="Max AI replies per contact/day"
            help="Daily cap per customer."
            name="maxAiRepliesPerContactPerDay"
            value={form.maxAiRepliesPerContactPerDay}
            min={0}
            max={100}
            onChange={onChange}
            disabled={!canManage}
          />
          <NumberField
            label="Max AI auto-replies per thread"
            help="Cap on sent auto-replies, not drafts."
            name="maxAiAutoRepliesPerThread"
            value={form.maxAiAutoRepliesPerThread}
            min={0}
            max={100}
            onChange={onChange}
            disabled={!canManage || !canManageAutoReply}
          />
          <NumberField
            label="Max AI auto-replies per contact/day"
            help="Daily cap per customer on sent auto-replies."
            name="maxAiAutoRepliesPerContactPerDay"
            value={form.maxAiAutoRepliesPerContactPerDay}
            min={0}
            max={100}
            onChange={onChange}
            disabled={!canManage || !canManageAutoReply}
          />
          <NumberField
            label="Max output tokens"
            help="Maximum generated reply length."
            name="maxOutputTokens"
            value={form.maxOutputTokens}
            min={1}
            max={2048}
            onChange={onChange}
            disabled={!canManage}
          />
          <NumberField
            label="Provider timeout seconds"
            help="Stop waiting after this many seconds."
            name="providerTimeoutSeconds"
            value={form.providerTimeoutSeconds}
            min={1}
            max={30}
            onChange={onChange}
            disabled={!canManage}
          />
          <NumberField
            label="Max context messages"
            help="Past messages included for context."
            name="maxContextMessages"
            value={form.maxContextMessages}
            min={1}
            max={100}
            onChange={onChange}
            disabled={!canManage}
          />
          <NumberField
            label="Temperature"
            help="Lower is more predictable. 0.2-0.4 works well for business replies."
            name="temperature"
            value={form.temperature}
            min={0}
            max={1.5}
            step={0.1}
            onChange={onChange}
            disabled={!canManage}
          />
        </div>
      </Disclosure>
    </div>
  );
}

export default function AiAgentSettingsPage() {
  const auth = useAuth() || {};
  const businessId = auth.effectiveBusinessId ||
    (auth.canSelectManagedBusinessScope ? auth.selectedBusinessId : auth.businessId) || auth.business?.id;
  const userId = auth.user?.id || auth.user?.userId;
  return <AiAgentSettingsEditor key={`${businessId || ""}:${userId || ""}`} auth={auth}
    businessId={businessId} userId={userId} />;
}

function AiAgentSettingsEditor({ auth, businessId, userId }) {
  const { search } = useLocation();
  const { can, hasAllAccess } = auth;
  const draftScope = React.useMemo(() => ({ businessId, userId }), [businessId, userId]);
  const canManage =
    Boolean(hasAllAccess) ||
    (typeof can === "function" && can(FK.AI_AGENT_SETTINGS_MANAGE));
  const canManageAutoReply =
    Boolean(hasAllAccess) ||
    (typeof can === "function" && can(FK.AI_AUTO_REPLY_MANAGE));

  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [loadState, setLoadState] = React.useState("loading");
  const [loadError, setLoadError] = React.useState("");
  const [saveError, setSaveError] = React.useState("");
  const [savedBase, setSavedBase] = React.useState(null);
  const [draftStored, setDraftStored] = React.useState(false);
  const [pendingDraft, setPendingDraft] = React.useState(null);
  const [conflict, setConflict] = React.useState(false);
  const [review, setReview] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const savingRef = React.useRef(false);
  const loadSequence = React.useRef({ value: 0 });
  const [updatedAtUtc, setUpdatedAtUtc] = React.useState(null);
  const [autoReplyRollout, setAutoReplyRollout] = React.useState(null);
  const [activationConfirmation, setActivationConfirmation] = React.useState(null);
  const [checkingReadiness, setCheckingReadiness] = React.useState(false);
  const [readinessError, setReadinessError] = React.useState("");
  const [responsePolicy, setResponsePolicy] = React.useState(
    BALANCED_RESPONSE_POLICY
  );
  const [responsePolicyWarnings, setResponsePolicyWarnings] = React.useState([]);
  const [currentStepId, setCurrentStepId] = React.useState(() => initialAiAgentSection(STEPS, "identity", search));
  const sectionHeadingRef = React.useRef(null);
  const previousStepRef = React.useRef(currentStepId);
  const [visitedStepIds, setVisitedStepIds] = React.useState(() => new Set([currentStepId]));
  React.useEffect(() => {
    const id = initialAiAgentSection(STEPS, "identity", search);
    setCurrentStepId(id);
    setVisitedStepIds(prev => new Set([...prev, id]));
  }, [search]);
  const [locationProofFallbackPolicyDirty, setLocationProofFallbackPolicyDirty] =
    React.useState(false);
  const locationProofFallbackPolicySavedRef = React.useRef(
    serializeLocationProofFallbackPolicy(DEFAULT_LOCATION_PROOF_FALLBACK_POLICY)
  );
  const hasUnsavedDraft = Boolean(savedBase && (
    !sameValue(form, savedBase.form) || !sameValue(responsePolicy, savedBase.policy)
  ));
  const mutationBlocked = !canManage || loadState !== "loaded" || saving || checkingReadiness || Boolean(pendingDraft) || conflict || Boolean(review);
  const dependencyErrors = capabilityDependencies(form);

  React.useEffect(() => {
    if (previousStepRef.current !== currentStepId) {
      sectionHeadingRef.current?.focus({ preventScroll: true });
      sectionHeadingRef.current?.scrollIntoView?.({ block: "nearest" });
      previousStepRef.current = currentStepId;
    }
  }, [currentStepId]);

  const snapshot = React.useCallback(data => {
    if (typeof data?.revision !== "string" || !data.revision.trim()
        || !RESPONSE_POLICY_FIELDS.every(field => typeof data?.responseCompositionPolicy?.[field.key] === "string")) {
      throw new Error("The complete AI setup could not be loaded. Refresh after updating the API, then retry.");
    }
    const normalized = normalizeResponsePolicy(data.responseCompositionPolicy);
    return { data, revision: data.revision, form: toForm(data), policy: normalized.policy, warnings: normalized.warnings };
  }, []);

  const acceptSaved = React.useCallback(next => {
    setSavedBase({ revision: next.revision, form: next.form, policy: next.policy });
    setForm(next.form);
    setResponsePolicy(next.policy);
    setResponsePolicyWarnings(next.warnings);
    locationProofFallbackPolicySavedRef.current = serializeLocationProofFallbackPolicy(next.form.locationProofFallbackPolicy);
    setLocationProofFallbackPolicyDirty(false);
    setAutoReplyRollout(next.data.autoReplyRollout || null);
    setUpdatedAtUtc(next.data.updatedAtUtc || null);
  }, []);

  const load = React.useCallback(async () => {
    const sequence = ++loadSequence.current.value;
    setLoadState("loading");
    setLoadError("");
    try {
      const next = snapshot(await getAiAgentSettings());
      if (sequence !== loadSequence.current.value) return;
      acceptSaved(next);
      const draft = readAiAgentDraft(SETTINGS_DRAFT_KEY, draftScope);
      setPendingDraft(draft?.form ? draft : null);
      setConflict(false);
      setReview(null);
      setLoadState("loaded");
    } catch (error) {
      if (sequence !== loadSequence.current.value) return;
      const message =
        error?.response?.data?.message || error.message || "Failed to load AI settings.";
      setLoadError(message);
      setLoadState("failed");
    }
  }, [snapshot, acceptSaved, draftScope]);

  React.useEffect(() => {
    const sequence = loadSequence.current;
    load();
    return () => { ++sequence.value; };
  }, [load]);

  React.useEffect(() => {
    if (loadState !== "loaded" || pendingDraft || review || saving) return;
    if (hasUnsavedDraft) {
      const stored = writeAiAgentDraft(SETTINGS_DRAFT_KEY, {
        form, policy: responsePolicy, base: savedBase, locationProofFallbackPolicyDirty,
      }, draftScope);
      setDraftStored(stored);
    } else {
      removeAiAgentDraft(SETTINGS_DRAFT_KEY, draftScope);
      setDraftStored(false);
    }
  }, [loadState, pendingDraft, review, saving, hasUnsavedDraft, form, responsePolicy, savedBase, locationProofFallbackPolicyDirty, draftScope]);

  React.useEffect(() => {
    if (!hasUnsavedDraft) return undefined;
    const protectReload = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", protectReload);
    return () => window.removeEventListener("beforeunload", protectReload);
  }, [hasUnsavedDraft]);

  const onChange = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const onLocationProofFallbackPolicyChange = (name, value) => {
    setLocationProofFallbackPolicyDirty(true);
    setForm(prev => {
      const next = {
        ...prev,
        locationProofFallbackPolicy: {
          ...(prev.locationProofFallbackPolicy || DEFAULT_LOCATION_PROOF_FALLBACK_POLICY),
          [name]: value,
        },
      };
      return next;
    });
  };

  const onResponsePolicyChange = (name, value) => {
    setResponsePolicy(prev => ({
      ...prev,
      [name]: value,
    }));
    setResponsePolicyWarnings([]);
  };

  const selectStep = id => {
    setCurrentStepId(id);
    setVisitedStepIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const currentStepIndex = STEPS.findIndex(step => step.id === currentStepId);
  const currentStep = STEPS[currentStepIndex];
  const isLastStep = currentStepIndex === STEPS.length - 1;
  const isFirstStep = currentStepIndex === 0;

  const goNext = () => {
    if (isLastStep) return;
    selectStep(STEPS[currentStepIndex + 1].id);
  };
  const goBack = () => {
    if (isFirstStep) return;
    selectStep(STEPS[currentStepIndex - 1].id);
  };

  const safeOnlyReadiness = capabilityReadiness(autoReplyRollout, "safeOnly", form);
  const autoReplyChecklist = safeOnlyReadiness.checks;
  const safeOnlySelectable = canManageAutoReply && safeOnlyReadiness.state === "Ready";
  const safeOnlyDisabledReason =
    safeOnlyReadiness.message || "Your role cannot change automatic sending.";
  const internalBetaSelectable =
    canManageAutoReply && capabilityReadiness(autoReplyRollout, "internalBeta", form).state === "Ready";
  const internalBetaDisabledReason =
    capabilityReadiness(autoReplyRollout, "internalBeta", form).message ||
    "InternalBeta is not available for this business.";
  const mediaAutoSendRolloutKnown = typeof autoReplyRollout?.mediaAutoSendEnabled === "boolean";
  const mediaAutoSendPlatformEnabled =
    autoReplyRollout?.mediaAutoSendEnabled === true;
  const mediaAutoSendDisabledReason =
    autoReplyRollout?.mediaAutoSendDisabledReason ||
    "Media auto-send only works when platform rollout is enabled and approved media assets exist.";
  const videoAutoSendPlatformEnabled =
    autoReplyRollout?.videoAutoSendEnabled === true;
  const videoAutoSendDisabledReason =
    autoReplyRollout?.videoAutoSendDisabledReason ||
    "Platform AI video auto-send switch is OFF.";

  const onSave = async ({ confirmed = false } = {}) => {
    if (!canManage) {
      toast.error("You do not have permission to update AI settings.");
      return;
    }
    if (mutationBlocked || savingRef.current) return;
    setSaveError("");
    const numericError = validateSettingsNumbers(form);
    if (numericError) {
      setSaveError(numericError);
      return;
    }

    if (dependencyErrors.length) {
      setSaveError(dependencyErrors[0].message);
      return;
    }
    const changes = newSendingCapabilities(form, savedBase.form);
    const fingerprint = JSON.stringify([form, responsePolicy, savedBase.revision]);
    if (changes.length) {
      if (!canManageAutoReply) { setSaveError("Your role cannot activate automatic sending."); return; }
      const blocked = changes.map(change => capabilityReadiness(autoReplyRollout, change.key, form)).find(item => item.state !== "Ready");
      if (blocked) { setSaveError(blocked.message || "Could not check readiness. Refresh checks."); return; }
      if (!confirmed || activationConfirmation?.fingerprint !== fingerprint) {
        setActivationConfirmation({ changes, fingerprint });
        return;
      }
    }

    const normalizedLocationProofFallbackPolicy =
      normalizeLocationProofFallbackPolicy(form.locationProofFallbackPolicy);
    const serializedLocationProofFallbackPolicy =
      serializeLocationProofFallbackPolicy(normalizedLocationProofFallbackPolicy);
    const locationProofFallbackPolicyChanged =
      locationProofFallbackPolicyDirty &&
      serializedLocationProofFallbackPolicy !==
        locationProofFallbackPolicySavedRef.current;
    if (
      locationProofFallbackPolicyChanged &&
      normalizedLocationProofFallbackPolicy.enableLocationOnlyFallback &&
      !String(normalizedLocationProofFallbackPolicy.locationOnlyTemplate || "").trim()
    ) {
      toast.error(
        "Please enter a location-only fallback template or disable this fallback."
      );
      return;
    }

    if (
      locationProofFallbackPolicyChanged &&
      normalizedLocationProofFallbackPolicy.enableGenericFallback &&
      !String(normalizedLocationProofFallbackPolicy.genericFallbackTemplate || "").trim()
    ) {
      toast.error(
        "Please enter a generic fallback template or disable this fallback."
      );
      return;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const payload = {
        expectedRevision: savedBase.revision,
        ...(!sameValue(responsePolicy, savedBase.policy) ? { responseCompositionPolicy: responsePolicy } : {}),
        isEnabled: Boolean(form.isEnabled),
        draftSuggestionEnabled: Boolean(form.draftSuggestionEnabled),
        fallbackDryRunEnabled: Boolean(form.fallbackDryRunEnabled),
        outsideFlowAiFallbackEnabled: Boolean(
          form.outsideFlowAiFallbackEnabled
        ),
        autoReplyAiRescueEnabled: Boolean(form.autoReplyAiRescueEnabled),
        leadQualificationDryRunEnabled: Boolean(
          form.leadQualificationDryRunEnabled
        ),
        leadQualificationAutoReplyMode: form.leadQualificationAutoReplyMode,
        leadQualificationPlannerMode: normalizePlannerModeForSave(
          form.leadQualificationPlannerMode
        ),
        leadQualificationControlledAnswerLiveMode:
          form.leadQualificationControlledAnswerLiveMode,
        naturalReplyMode: form.naturalReplyMode ?? null,
        aiAutoReplyMode: form.aiAutoReplyMode,
        requireSourceBackedAutoReply: Boolean(
          form.requireSourceBackedAutoReply
        ),
        maxAiAutoRepliesPerThread:
          Number(form.maxAiAutoRepliesPerThread),
        maxAiAutoRepliesPerContactPerDay:
          Number(form.maxAiAutoRepliesPerContactPerDay),
        businessHoursOnly: Boolean(form.businessHoursOnly),
        handoverOnNoSource: Boolean(form.handoverOnNoSource),
        safeNoSourceFallbackEnabled: Boolean(form.safeNoSourceFallbackEnabled),
        approvedMediaAutoSendEnabled: Boolean(
          form.approvedMediaAutoSendEnabled
        ),
        maxSafeNoSourceFallbacksPerThread: Number(form.maxSafeNoSourceFallbacksPerThread),
        handoverOnSensitiveTopic: Boolean(form.handoverOnSensitiveTopic),
        botDisplayName: fieldValue(form.botDisplayName),
        botRoleName: fieldValue(form.botRoleName),
        disclosureMode: form.disclosureMode,
        greetingTemplate: fieldValue(form.greetingTemplate),
        industryType: form.industryType,
        businessGoal: fieldValue(form.businessGoal),
        languageMode: form.languageMode,
        tone: form.tone,
        replyLength: form.replyLength,
        emojiUsage: form.emojiUsage,
        requireSourceBackedAnswer: Boolean(form.requireSourceBackedAnswer),
        allowGeneralKnowledgeFallback: Boolean(form.allowGeneralKnowledgeFallback),
        maxRetrievedChunks: Number(form.maxRetrievedChunks),
        noSourceBehavior: form.noSourceBehavior,
        allowedTopicsNotes: fieldValue(form.allowedTopicsNotes),
        blockedTopicsNotes: fieldValue(form.blockedTopicsNotes),
        sensitiveTopicPolicy: form.sensitiveTopicPolicy,
        escalationRules: fieldValue(form.escalationRules),
        handoverMessage: fieldValue(form.handoverMessage),
        photoReceivedMessage: fieldValue(form.photoReceivedMessage),
        leadSessionTimeoutHours:
          form.leadSessionTimeoutHours != null
            ? Number(form.leadSessionTimeoutHours)
            : null,
        leadRestartPolicy: form.leadRestartPolicy || "AdminOnly",
        maxAiRepliesPerThread: Number(form.maxAiRepliesPerThread),
        maxAiRepliesPerContactPerDay:
          Number(form.maxAiRepliesPerContactPerDay),
        maxOutputTokens: Number(form.maxOutputTokens),
        providerTimeoutSeconds: Number(form.providerTimeoutSeconds),
        maxContextMessages: Number(form.maxContextMessages),
        temperature: Number(form.temperature),
        additionalInstructions: fieldValue(form.additionalInstructions),
      };

      if (locationProofFallbackPolicyChanged) {
        payload.locationProofFallbackPolicyJson =
          serializedLocationProofFallbackPolicy;
      }

      const result = await updateAiAgentSettings(payload);
      acceptSaved(snapshot(result));
      removeAiAgentDraft(SETTINGS_DRAFT_KEY, draftScope);
      setActivationConfirmation(null);
      toast.success(
        locationProofFallbackPolicyChanged
          ? "Location proof fallback wording saved."
          : "AI settings updated."
      );
    } catch (error) {
      const message =
        error?.response?.data?.message || error.message || "Failed to update AI settings.";
      setSaveError(message);
      setActivationConfirmation(null);
      if ([409, 428].includes(error?.response?.status)) setConflict(true);
      toast.error(message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const reviewLatest = async (draft = null) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const latest = snapshot(await getAiAgentSettings());
      const local = draft ? {
        form: { ...savedBase.form, ...draft.form }, policy: draft.policy || savedBase.policy,
      } : { form, policy: responsePolicy };
      const rows = buildSettingsReview(draft?.base || savedBase, local, latest);
      setReview({ latest, rows });
      setPendingDraft(null);
      setSaveError("");
    } catch (error) {
      setSaveError(error?.response?.data?.message || error.message || "Could not refresh saved settings. Your edits are preserved.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const discardDraft = () => {
    removeAiAgentDraft(SETTINGS_DRAFT_KEY, draftScope);
    setPendingDraft(null);
  };

  const restoreDraft = () => {
    if (pendingDraft?.base?.revision !== savedBase.revision) {
      reviewLatest(pendingDraft);
      return;
    }
    const restoredForm = { ...savedBase.form, ...pendingDraft.form };
    restoredForm.leadQualificationPlannerMode = normalizePlannerModeForDisplay(restoredForm.leadQualificationPlannerMode);
    if (!pendingDraft.locationProofFallbackPolicyDirty) restoredForm.locationProofFallbackPolicy = savedBase.form.locationProofFallbackPolicy;
    setForm(restoredForm);
    setResponsePolicy(pendingDraft.policy || savedBase.policy);
    setLocationProofFallbackPolicyDirty(Boolean(pendingDraft.locationProofFallbackPolicyDirty));
    setPendingDraft(null);
  };

  const finishReview = keepEdits => {
    const next = applySettingsReview(review.latest, keepEdits ? review.rows : []);
    acceptSaved(review.latest);
    setForm(next.form);
    setResponsePolicy(next.policy);
    setLocationProofFallbackPolicyDirty(!sameValue(next.form.locationProofFallbackPolicy, review.latest.form.locationProofFallbackPolicy));
    setReview(null);
    setConflict(false);
    setSaveError("");
    removeAiAgentDraft(SETTINGS_DRAFT_KEY, draftScope);
  };

  const refreshReadiness = async () => {
    if (savingRef.current || checkingReadiness) return;
    const sequence = ++loadSequence.current.value;
    setCheckingReadiness(true);
    setReadinessError("");
    setAutoReplyRollout(null);
    try {
      const latest = snapshot(await getAiAgentSettings());
      if (sequence !== loadSequence.current.value) return;
      if (latest.revision !== savedBase.revision) {
        setConflict(true);
        setSaveError("Saved settings changed. Your edits are preserved; review the latest settings before saving.");
      } else {
        setAutoReplyRollout(latest.data.autoReplyRollout || null);
      }
    } catch (error) {
      if (sequence !== loadSequence.current.value) return;
      setReadinessError(error?.response?.data?.message || error.message || "Refresh failed.");
    } finally {
      if (sequence === loadSequence.current.value) setCheckingReadiness(false);
    }
  };

  const renderStepContent = () => {
    if (!currentStep) return null;
    switch (currentStep.id) {
      case "identity":
        return <div className="space-y-6">
          <IdentityStep form={form} onChange={onChange} canManage={canManage} />
          <Disclosure title="Voice & language" hint="Language, tone and reply length">
            <VoiceStep form={form} onChange={onChange} canManage={canManage} />
          </Disclosure>
        </div>;
      case "knowledge":
        return (
          <div className="space-y-5">
            <WorkspaceLinks auth={auth} itemKeys={["ai_agent_knowledge_base", "ai_agent_media_assets"]} label="Business information libraries" />
            <KnowledgeStep form={form} onChange={onChange} canManage={canManage} />
          </div>
        );
      case "qualification":
        return <LeadCollectionStep form={form} onChange={onChange} canManage={canManage}
          canManageAutoReply={canManageAutoReply} auth={auth} />;
      case "safety":
        return (
          <div className="space-y-5">
            <SafetyStep form={form} onChange={onChange} canManage={canManage} />
            <div className="space-y-3 border-t border-slate-200 pt-4">
              <h3 className="text-sm font-medium text-slate-900">Follow-up configuration</h3>
              <p className="text-xs text-slate-500">Incomplete-enquiry reminders and event-based delayed replies are configured separately in the qualification overview.</p>
              <WorkspaceLinks auth={auth} itemKeys={["ai_agent_lead_qualification"]} label="Follow-up configuration" />
            </div>
          </div>
        );
      case "autoreply":
        return (
          <div className="space-y-5">
          <WorkspaceLinks auth={auth} itemKeys={["ai_agent_test_console"]} label="Test saved setup" />
          <AutoReplyStep
            form={form}
            onChange={onChange}
            onLocationProofFallbackPolicyChange={
              onLocationProofFallbackPolicyChange
            }
            responsePolicy={responsePolicy}
            onResponsePolicyChange={onResponsePolicyChange}
            setResponsePolicy={setResponsePolicy}
            setResponsePolicyWarnings={setResponsePolicyWarnings}
            responsePolicyWarnings={responsePolicyWarnings}
            responsePolicySaving={saving}
            onSaveResponsePolicy={() => onSave()}
            canManage={canManage}
            canManageAutoReply={canManageAutoReply}
            autoReplyChecklist={autoReplyChecklist}
            safeOnlySelectable={safeOnlySelectable}
            safeOnlyDisabledReason={safeOnlyDisabledReason}
            internalBetaSelectable={internalBetaSelectable}
            internalBetaDisabledReason={internalBetaDisabledReason}
            mediaAutoSendRolloutKnown={mediaAutoSendRolloutKnown}
            mediaAutoSendPlatformEnabled={mediaAutoSendPlatformEnabled}
            mediaAutoSendDisabledReason={mediaAutoSendDisabledReason}
            videoAutoSendPlatformEnabled={videoAutoSendPlatformEnabled}
            videoAutoSendDisabledReason={videoAutoSendDisabledReason}
            dependencyErrors={dependencyErrors}
          />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 p-6">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-lg border border-slate-200 bg-white">
        <header className="border-b border-slate-200 px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-600" />
                <h1 className="text-lg font-semibold text-slate-900">
                  AI Agent Setup
                </h1>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                Set up business answers, enquiry collection and team assistance.
              </p>
              {updatedAtUtc ? (
                <p className="mt-1 text-xs text-slate-500">
                  Last saved: {new Date(updatedAtUtc).toLocaleString()}
                </p>
              ) : null}
            </div>
            <span
              className={`hidden rounded-full px-3 py-1 text-xs font-medium md:inline-flex ${
                savedBase?.form.isEnabled
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              <Bot className="mr-1.5 h-3.5 w-3.5" />
              {savedBase ? (savedBase.form.isEnabled ? "Saved: AI Agent is ON" : "Saved: AI Agent is OFF") : "Settings not loaded"}
            </span>
          </div>
          {hasUnsavedDraft ? (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {draftStored ? "Unsaved changes are kept in this browser tab until you save them." : "Browser draft recovery is unavailable. Save your changes before leaving this page."}
            </p>
          ) : null}
        </header>
        <div className="flex items-stretch">
          {loadState === "loaded" ? <aside className="w-64 flex-none border-r border-slate-200 bg-slate-50 p-3">
            <p className="px-3 pb-2 pt-1 text-xs font-medium text-slate-500">Setup</p>
            <Stepper steps={STEPS} currentId={currentStepId} visitedIds={visitedStepIds} onSelect={selectStep}
              disabled={saving || Boolean(activationConfirmation)} />
            <div className="mt-5 border-t border-slate-200 pt-4">
              <AiAgentReadinessSummary compact rollout={autoReplyRollout} form={form} dirty={hasUnsavedDraft}
                canManageSending={canManageAutoReply} onRefresh={refreshReadiness} checking={checkingReadiness}
                disabled={saving || Boolean(activationConfirmation) || Boolean(pendingDraft) || Boolean(review)} error={readinessError} />
              <button type="button" onClick={() => selectStep("autoreply")} disabled={saving || Boolean(activationConfirmation)}
                className="mx-2 mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-700 underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600">Review requirements<ArrowRight aria-hidden="true" className="h-3 w-3" /></button>
            </div>
            <div className="mt-4 border-t border-slate-200 px-1 pt-4">
              <WorkspaceLinks auth={auth} itemKeys={["ai_agent_usage", "ai_agent_lead_qualification_debug"]} label="Operations & advanced support" />
            </div>
          </aside> : null}
          <div className="min-w-0 flex-1 space-y-4 p-6">
        {pendingDraft ? (
          <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p>A browser draft is available for this business and your account. It has not been applied.</p>
            {pendingDraft.base?.revision !== savedBase?.revision ? (
              <p className="mt-2">Saved settings changed since this draft began. Review each change before applying it.</p>
            ) : null}
            <div className="mt-3 flex gap-3">
              <button type="button" onClick={restoreDraft} disabled={saving} className="rounded border border-amber-400 px-3 py-2 font-medium disabled:opacity-50">
                {pendingDraft.base?.revision === savedBase?.revision ? "Restore draft" : "Review draft"}
              </button>
              <button type="button" onClick={discardDraft} disabled={saving} className="rounded border border-amber-400 px-3 py-2">Discard draft</button>
            </div>
          </div>
        ) : null}

        {saveError ? <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{saveError}</div> : null}
        {conflict && !review ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p>Your edits are preserved. Refresh the saved values to compare and merge changes before saving again.</p>
            <button type="button" onClick={() => reviewLatest()} disabled={saving} className="mt-3 rounded border border-amber-400 px-3 py-2 font-medium disabled:opacity-50">Review latest settings</button>
          </div>
        ) : null}
        {review ? (
          <section aria-label="Review saved settings" className="rounded-lg border border-amber-200 bg-white p-4 text-sm">
            <h2 className="font-semibold text-slate-900">Review your changes against the latest saved settings</h2>
            <p className="mt-2 text-slate-600">Only your edited fields appear here. Conflicting fields start with the saved value selected. Applying this review updates your draft; it does not save yet.</p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full table-fixed text-left">
                <thead><tr><th className="p-2">Setting</th><th className="p-2">Latest saved</th><th className="p-2">Your edit</th><th className="p-2">Use</th></tr></thead>
                <tbody>{review.rows.map(row => (
                  <tr key={row.id} className="border-t border-slate-200">
                    <th scope="row" className="break-words p-2">{row.label}{row.conflict ? " (conflict)" : ""}</th>
                    <td className="break-words p-2">{displaySettingsValue(row.savedValue)}</td>
                    <td className="break-words p-2">{displaySettingsValue(row.value)}</td>
                    <td className="p-2"><select aria-label={`Choose value for ${row.label}`} value={row.choice}
                      onChange={event => { const choice = event.target.value; setReview(prev => ({ ...prev, rows: prev.rows.map(item => item.id === row.id ? { ...item, choice } : item) })); }}
                      className="w-full rounded border border-slate-300 p-2">
                      <option value="saved">Saved value</option><option value="mine">My edit</option>
                    </select></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              <button type="button" onClick={() => finishReview(true)} className="rounded border border-emerald-600 px-3 py-2 font-medium text-emerald-700">Apply reviewed changes</button>
              <button type="button" onClick={() => finishReview(false)} className="rounded border border-slate-300 px-3 py-2">Discard edits and use latest</button>
            </div>
          </section>
        ) : null}

        {loadState === "loading" ? (
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <p className="text-sm text-slate-500">Loading settings...</p>
          </div>
        ) : loadState === "failed" ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-800">
            <p>{loadError}</p>
            <p className="mt-2">Settings cannot be changed until the complete saved setup is loaded.</p>
            <button type="button" onClick={load} className="mt-3 rounded border border-red-300 px-3 py-2 font-medium">Retry loading settings</button>
          </div>
        ) : (
          <>
            <div>
              <StepHeader step={currentStep} headingRef={sectionHeadingRef} />
              <fieldset disabled={saving || checkingReadiness || Boolean(activationConfirmation) || Boolean(pendingDraft) || conflict || Boolean(review)} className="mt-5 min-w-0 border-0 p-0">{renderStepContent()}</fieldset>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
              <button
                type="button"
                onClick={goBack}
                disabled={isFirstStep}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onSave()}
                  disabled={mutationBlocked}
                  className={`inline-flex h-10 items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium ${
                    mutationBlocked
                      ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                      : "border-emerald-600 bg-white text-emerald-700 hover:bg-emerald-50"
                  }`}
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Saving..." : isLastStep ? "Save & Finish" : "Save"}
                </button>

                {!isLastStep ? (
                  <button
                    type="button"
                    onClick={goNext}
                    className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>

            {activationConfirmation ? <AiAgentActivationDialog changes={activationConfirmation.changes} saving={saving}
              onCancel={() => setActivationConfirmation(null)} onConfirm={() => onSave({ confirmed: true })} /> : null}

          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}
